import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";
import JoditEditor from "jodit-react";
import {
  FiMapPin,
  FiPlus,
  FiTrash2,
  FiCheck,
  FiSearch,
  FiUser,
  FiHome,
  FiUploadCloud,
  FiArrowRight,
  FiArrowLeft,
  FiLayers,
  FiCheckCircle,
} from "react-icons/fi";

// State to City Data for instant offline lookup
const INDIA_STATE_CITIES = {
  "Madhya Pradesh": ["Gwalior", "Indore", "Bhopal", "Jabalpur", "Ujjain", "Sagar", "Dewas", "Satna", "Ratlam", "Rewa"],
  Delhi: ["New Delhi", "Delhi", "North Delhi", "South Delhi", "West Delhi", "East Delhi"],
  Maharashtra: ["Mumbai", "Pune", "Nagpur", "Thane", "Nashik", "Aurangabad", "Navi Mumbai", "Solapur"],
  "Uttar Pradesh": ["Noida", "Greater Noida", "Ghaziabad", "Lucknow", "Kanpur", "Agra", "Varanasi", "Prayagraj", "Meerut"],
  Haryana: ["Gurugram", "Faridabad", "Panipat", "Ambala", "Karnal", "Rohtak", "Hisar", "Sonipat"],
  Karnataka: ["Bengaluru", "Mysuru", "Hubballi", "Mangaluru", "Belagavi", "Davangere"],
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem", "Tirunelveli"],
  Gujarat: ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar", "Gandhinagar"],
  Rajasthan: ["Jaipur", "Jodhpur", "Kota", "Bikaner", "Ajmer", "Udaipur", "Bhilwara", "Alwar"],
};

const INDIA_STATES = Object.keys(INDIA_STATE_CITIES);

const residentialTypes = [
  "Home", "apartment", "townhouse", "villa-compound", "land", "building",
  "villa", "penthouse", "hotel-apartment", "floor", "studio"
];
const commercialTypes = [
  "office", "warehouse", "industrial-land", "showroom", "shop", "labour-camp",
  "bulk-unit", "factory", "mixed-use-land", "other-commercial", "floor", "building", "villa"
];

const amenityList = [
  "Swimming Pool", "Gym", "Power Backup", "Lift/Elevator", "Security",
  "24/7 Security", "CCTV Surveillance", "Garden", "Covered Parking",
  "Clubhouse", "Children's Play Area", "Wi-Fi Included", "Air Conditioning",
  "EV Charging Station", "Yoga Studio", "Intercom", "Fire Fighting System"
];

const furnishingItemsList = [
  "Air Conditioner", "Bed", "Wardrobe", "TV", "Refrigerator", "Sofa",
  "Dining Table", "Microwave", "Washing Machine", "Geyser", "Modular Kitchen",
  "Curtains", "Fan", "Exhaust Fan", "Water Purifier"
];

const steps = [
  { id: 1, title: "BASIC INFO", desc: "Type & Category" },
  { id: 2, title: "LOCATION", desc: "City & Address" },
  { id: 3, title: "SPECIFICATIONS", desc: "Area & Pricing" },
  { id: 4, title: "AMENITIES & LEGAL", desc: "RERA & Features" },
  { id: 5, title: "MEDIA", desc: "Photos & Description" },
];

export default function AddPropertyPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchingUser, setSearchingUser] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [filteredCities, setFilteredCities] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [furnishingItems, setFurnishingItems] = useState([]);
  const [aroundProject, setAroundProject] = useState([{ name: "", details: "" }]);
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    ownerPhone: "",
    listingCategory: "buy",
    property: "residential",
    propertyType: "Home",
    isBroker: "no",
    rera: "",
    city: "",
    pincode: "",
    houseNumber: "",
    localityArea: "",
    propertyAddress: "",
    price: "",
    area: "",
    bedRoom: "2",
    bathrooms: "2",
    balcony: "1",
    parking: "1",
    furnishing: "semi-furnished",
    securityDeposit: "",
    availableFrom: "",
    uploadedPhotos: [],
  });

  // Load dynamic categories if present
  useEffect(() => {
    const loadCats = async () => {
      try {
        const res = await makeRequestAuth("post", "/admin/getPropertyCategoryList", {});
        if (res?.data?.list) {
          setCategories(res.data.list);
        }
      } catch (e) {
        // quiet fallback
      }
    };
    loadCats();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleStateChange = (stateName) => {
    setSelectedState(stateName);
    setForm((prev) => ({ ...prev, city: "" }));
    setFilteredCities(INDIA_STATE_CITIES[stateName] || []);
  };

  // 🔍 User Search & Autofill
  const handleSearchUser = async () => {
    if (!form.ownerPhone || !form.ownerPhone.trim()) {
      return showErrorToast("Please enter phone number or email first!");
    }

    try {
      setSearchingUser(true);
      setFoundUser(null);

      const res = await makeRequestAuth("post", "/admin/getUserBySearch", {
        search: form.ownerPhone.trim(),
      });

      if (!res?.error && res?.data) {
        const u = res.data;
        setFoundUser(u);
        showSuccessToast(`User Found: ${u.name || u.phone}`);

        // Autofill user details into form
        setForm((prev) => ({
          ...prev,
          city: u.city || prev.city,
          pincode: u.pincode || prev.pincode,
          propertyAddress: u.address || prev.propertyAddress,
        }));

        if (u.state) {
          setSelectedState(u.state);
          setFilteredCities(INDIA_STATE_CITIES[u.state] || [u.city]);
        }
      } else {
        setFoundUser({ notFound: true });
        showErrorToast(res?.message || "User not found on web");
      }
    } catch (err) {
      setFoundUser({ notFound: true });
      showErrorToast(err.message || "User not found");
    } finally {
      setSearchingUser(false);
    }
  };

  // 📍 GPS Location Detection
  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      return showErrorToast("Geolocation is not supported by your browser");
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await res.json();

          if (data && data.address) {
            const addr = data.address;
            const fetchedState = addr.state || "";
            const fetchedCity = addr.city || addr.town || addr.district || "";
            const fetchedPincode = addr.postcode || "";
            const fetchedLocality =
              addr.suburb || addr.neighbourhood || addr.residential || addr.city_district || "";
            const fetchedRoad = addr.road || "";
            const fullAddress = [fetchedRoad, fetchedLocality, fetchedCity].filter(Boolean).join(", ");

            if (fetchedState) {
              setSelectedState(fetchedState);
              setFilteredCities(INDIA_STATE_CITIES[fetchedState] || [fetchedCity]);
            }

            setForm((prev) => ({
              ...prev,
              city: fetchedCity || prev.city,
              pincode: fetchedPincode || prev.pincode,
              localityArea: fetchedLocality || prev.localityArea,
              propertyAddress: fullAddress || prev.propertyAddress,
            }));
            showSuccessToast("Location detected successfully!");
          } else {
            showErrorToast("Could not determine detailed address");
          }
        } catch (error) {
          showErrorToast("Failed to fetch location name");
        } finally {
          setIsLocating(false);
        }
      },
      () => {
        setIsLocating(false);
        showErrorToast("Unable to retrieve location. Please check browser permissions.");
      }
    );
  };

  const toggleAmenity = (item) => {
    setAmenities((prev) =>
      prev.includes(item) ? prev.filter((a) => a !== item) : [...prev, item]
    );
  };

  const toggleFurnishingItem = (item) => {
    setFurnishingItems((prev) =>
      prev.includes(item) ? prev.filter((f) => f !== item) : [...prev, item]
    );
  };

  const handleAroundChange = (index, field, val) => {
    const updated = [...aroundProject];
    updated[index][field] = val;
    setAroundProject(updated);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    try {
      setUploading(true);
      const res = await uploadFiles({ files, endpoint: "/uploadMultipleImages" });
      if (res?.error) {
        return showErrorToast(res.message || "Failed to upload images");
      }
      const images = res?.data?.map((i) => i.imageUrl) || [];
      setForm((prev) => ({
        ...prev,
        uploadedPhotos: [...prev.uploadedPhotos, ...images],
      }));
      showSuccessToast(`${images.length} Image(s) uploaded successfully`);
    } catch (err) {
      showErrorToast("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setForm((prev) => ({
      ...prev,
      uploadedPhotos: prev.uploadedPhotos.filter((_, i) => i !== index),
    }));
  };

  const validateStep = (currentStep) => {
    const errs = {};
    if (currentStep === 1) {
      if (!form.listingCategory) errs.listingCategory = "Required";
      if (!form.property) errs.property = "Required";
      if (!form.propertyType) errs.propertyType = "Required";
      if (form.isBroker === "yes" && !form.rera.trim()) errs.rera = "RERA number is required";
    }
    if (currentStep === 2) {
      if (!form.city) errs.city = "City is required";
      if (!form.pincode) errs.pincode = "Pincode is required";
      if (!form.localityArea) errs.localityArea = "Locality is required";
      if (!form.propertyAddress) errs.propertyAddress = "Address is required";
    }
    if (currentStep === 3) {
      if (!form.price) errs.price = "Valid price required";
      if (!form.area) errs.area = "Valid area required";
    }
    if (currentStep === 5) {
      if (form.uploadedPhotos.length === 0) errs.uploadedPhotos = "Upload at least 1 photo";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(5)) return;

    try {
      setLoading(true);
      const payload = {
        ...form,
        amenities,
        furnishingItems,
        aroundProject: aroundProject.filter((a) => a.name.trim() && a.details.trim()),
        description,
      };

      const res = await makeRequestAuth("post", "/admin/createProperty", payload);

      if (!res?.error) {
        showSuccessToast("Property Published Successfully!");
        setTimeout(() => navigate("/properties/approved"), 1200);
      } else {
        showErrorToast(res.message || "Failed to create property");
      }
    } catch (err) {
      showErrorToast(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Human readable currency helper
  const formatPriceWords = (amount) => {
    if (!amount) return "";
    const num = Number(amount);
    if (isNaN(num) || num <= 0) return "";
    if (num >= 10000000) return `₹ ${(num / 10000000).toFixed(2)} Crore`;
    if (num >= 100000) return `₹ ${(num / 100000).toFixed(2)} Lakh`;
    if (num >= 1000) return `₹ ${(num / 1000).toFixed(2)} Thousand`;
    return `₹ ${num.toLocaleString("en-IN")}`;
  };

  // Property types to show
  const dynamicFiltered = categories.filter(
    (c) => c.propertySector === form.property || c.propertySector === "both"
  );
  const dynamicTypeObjects = dynamicFiltered.map((c) => ({
    label: c.name,
    value: c.propertyTypeKey,
  }));
  const fallbackStaticTypes = (
    form.property === "commercial" ? commercialTypes : residentialTypes
  ).map((t) => ({ label: t.replace("-", " "), value: t }));
  const displayedTypes = dynamicTypeObjects.length > 0 ? dynamicTypeObjects : fallbackStaticTypes;

  const progressPercent = Math.round((step / steps.length) * 100);

  return (
    <div className="min-h-screen bg-[#F6F8FA] p-4 md:p-8">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* TOP HEADER */}
      <div className="max-w-6xl mx-auto flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#24ADD7] flex items-center justify-center text-white shadow-md shadow-cyan-200">
              <FiHome className="text-base" />
            </div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight">Add New Property</h1>
          </div>
          <p className="text-xs font-semibold text-gray-400 mt-1">
            Complete property details in 5 guided steps
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-black uppercase tracking-wider shadow-sm transition flex items-center gap-1.5"
        >
          <FiArrowLeft /> Back to List
        </button>
      </div>

      <div className="max-w-6xl mx-auto space-y-6">

        {/* 👤 TOP USER MAPPING BOX (HERO SEARCH CARD) */}
        <div className="bg-white rounded-3xl p-6 border border-blue-100 shadow-[0_10px_30px_rgba(36,173,215,0.08)] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-[#24ADD7]/10 to-transparent rounded-full blur-2xl pointer-events-none" />

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-lg bg-blue-50 text-[#24ADD7] flex items-center justify-center text-xs font-black">
                <FiUser />
              </span>
              <label className="text-xs font-black uppercase tracking-wider text-gray-800">
                Property Owner Phone Number / Email (Optional)
              </label>
            </div>
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-tight">
              Assign to registered user
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                name="ownerPhone"
                placeholder="Enter owner phone or email (e.g. 7688822410)..."
                value={form.ownerPhone}
                onChange={(e) => {
                  handleChange(e);
                  setFoundUser(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearchUser();
                  }
                }}
                className="w-full px-4 py-3 pl-11 bg-gray-50/80 hover:bg-white focus:bg-white border-2 border-gray-100 focus:border-[#24ADD7] rounded-2xl text-sm font-bold text-gray-800 outline-none transition-all placeholder:text-gray-400 placeholder:font-normal"
              />
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
            </div>

            <button
              type="button"
              disabled={searchingUser}
              onClick={handleSearchUser}
              className="px-6 py-3 bg-[#24ADD7] hover:bg-[#1b8fae] active:scale-95 text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-cyan-500/25 transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {searchingUser ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <FiSearch /> Search User
                </>
              )}
            </button>
          </div>

          {/* USER FOUND BADGE */}
          {foundUser && !foundUser.notFound && (
            <div className="mt-4 bg-emerald-50/90 border border-emerald-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-in fade-in duration-300">
              <div className="flex items-start sm:items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-emerald-500 text-white flex items-center justify-center text-sm shadow-sm">
                  <FiCheckCircle />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-black uppercase tracking-wide text-emerald-800">
                      User Verified & Linked
                    </p>
                    <span className="bg-emerald-200/70 text-emerald-900 text-[9px] font-black px-2 py-0.5 rounded-full uppercase">
                      Web Account
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 font-bold mt-0.5">
                    Name: <span className="text-gray-900">{foundUser.name || "N/A"}</span> | Phone:{" "}
                    <span className="text-gray-900">{foundUser.phone}</span>
                    {foundUser.email && (
                      <>
                        {" "}
                        | Email: <span className="text-gray-900">{foundUser.email}</span>
                      </>
                    )}
                  </p>
                  {foundUser.city && (
                    <p className="text-[11px] font-semibold text-emerald-700 mt-0.5">
                      📍 Location: {foundUser.city}
                      {foundUser.state ? `, ${foundUser.state}` : ""} (Details Autofilled)
                    </p>
                  )}
                </div>
              </div>
              <span className="self-end sm:self-auto bg-emerald-600 text-white text-[10px] font-black px-3 py-1 rounded-xl uppercase tracking-wider shadow-sm">
                Active Owner
              </span>
            </div>
          )}

          {/* USER NOT FOUND BADGE */}
          {foundUser?.notFound && (
            <div className="mt-4 bg-amber-50/90 border border-amber-200 rounded-2xl p-3.5 flex items-center gap-3 animate-in fade-in duration-300">
              <span className="text-amber-600 font-black text-sm">⚠️</span>
              <p className="text-xs font-bold text-amber-800">
                Is phone number ya email se koi web account nahi mila. Yeh property{" "}
                <span className="font-black text-amber-950">Admin</span> ke under create hogi.
              </p>
            </div>
          )}
        </div>

        {/* MAIN WIZARD CONTAINER (2-COLUMN DESIGN) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">

          {/* LEFT STEPPER COLUMN */}
          <div className="md:col-span-4 bg-white rounded-3xl p-6 border border-gray-100 shadow-sm sticky top-6">
            <div className="mb-6">
              <div className="flex justify-between items-center text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                <span>Progress</span>
                <span className="text-[#24ADD7] font-black">{progressPercent}% COMPLETE</span>
              </div>
              <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-gradient-to-r from-[#24ADD7] to-cyan-400 h-full rounded-full transition-all duration-500 shadow-sm"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            <div className="space-y-3">
              {steps.map((s) => {
                const isActive = step === s.id;
                const isDone = step > s.id;
                return (
                  <div
                    key={s.id}
                    onClick={() => {
                      if (s.id < step) setStep(s.id);
                    }}
                    className={`flex items-center gap-3.5 p-3 rounded-2xl transition-all cursor-pointer group ${
                      isActive
                        ? "bg-blue-50/70 border-2 border-[#24ADD7]/30 shadow-sm"
                        : "hover:bg-gray-50 border-2 border-transparent"
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center font-black text-xs transition-all ${
                        isActive
                          ? "bg-[#24ADD7] text-white shadow-md shadow-cyan-300/50 scale-105"
                          : isDone
                          ? "bg-emerald-500 text-white"
                          : "bg-gray-100 text-gray-400 group-hover:text-gray-600"
                      }`}
                    >
                      {isDone ? <FiCheck className="text-sm" /> : s.id}
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-xs font-black uppercase tracking-wider transition-colors ${
                          isActive ? "text-[#24ADD7]" : isDone ? "text-gray-900" : "text-gray-400"
                        }`}
                      >
                        {s.title}
                      </p>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 pt-4 border-t border-gray-100 text-center">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                Step {step} of 5
              </span>
            </div>
          </div>

          {/* RIGHT STEP CONTENT */}
          <div className="md:col-span-8 bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col justify-between min-h-[580px]">
            <div className="p-6 md:p-8 flex-1">

              {/* STEP 1: BASIC INFO */}
              {step === 1 && (
                <div className="space-y-6 animate-in fade-in duration-300">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 tracking-tight">Basic Details</h2>
                    <p className="text-xs font-bold text-gray-400 mt-0.5 uppercase tracking-wide">
                      Select listing purpose and property sector
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* LISTING PURPOSE */}
                    <div>
                      <label className="label">
                        Listing Purpose <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2 mt-2 p-1.5 bg-gray-50 rounded-2xl border border-gray-200">
                        {[
                          { label: "Sell", value: "buy" },
                          { label: "Rent Out", value: "rent" },
                        ].map((cat) => (
                          <button
                            key={cat.value}
                            type="button"
                            onClick={() => setForm({ ...form, listingCategory: cat.value })}
                            className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                              form.listingCategory === cat.value
                                ? "bg-[#24ADD7] text-white shadow-md shadow-cyan-300/40"
                                : "text-gray-700 hover:text-gray-900"
                            }`}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* PROPERTY SECTOR */}
                    <div>
                      <label className="label">
                        Property Sector <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="property"
                        value={form.property}
                        onChange={handleChange}
                        className="input-field mt-2 cursor-pointer font-bold"
                      >
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                      </select>
                    </div>
                  </div>

                  {/* SPECIFIC PROPERTY TYPE */}
                  <div>
                    <label className="label">
                      Specific Property Type <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-2.5">
                      {displayedTypes.map((item, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setForm({ ...form, propertyType: item.value })}
                          className={`p-3 border-2 rounded-2xl text-xs font-black uppercase tracking-tight transition-all cursor-pointer ${
                            form.propertyType === item.value
                              ? "border-[#24ADD7] bg-blue-50 text-[#24ADD7] shadow-sm shadow-cyan-200"
                              : "border-gray-200 text-gray-700 bg-gray-50/50 hover:border-blue-200 hover:bg-white"
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ARE YOU A BROKER */}
                  <div className="pt-2">
                    <label className="label">
                      Are you a broker? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2 mt-2 p-1.5 bg-gray-50 rounded-2xl border border-gray-200 w-full sm:w-1/2">
                      {["no", "yes"].map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setForm({ ...form, isBroker: opt })}
                          className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all cursor-pointer ${
                            form.isBroker === opt
                              ? "bg-[#24ADD7] text-white shadow-md shadow-cyan-300/40"
                              : "text-gray-700 hover:text-gray-900"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {form.isBroker === "yes" && (
                    <div className="animate-in fade-in duration-200">
                      <label className="label">
                        RERA Registration Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="rera"
                        placeholder="Enter valid RERA number..."
                        value={form.rera}
                        onChange={handleChange}
                        className="input-field mt-2"
                      />
                      {errors.rera && (
                        <p className="text-[10px] text-red-500 font-bold mt-1 uppercase">
                          {errors.rera}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2: LOCATION */}
              {step === 2 && (
                <div className="space-y-6 animate-in fade-in duration-300">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <h2 className="text-2xl font-black text-gray-900 tracking-tight">Location Details</h2>
                      <p className="text-xs font-bold text-gray-400 mt-0.5 uppercase tracking-wide">
                        Pinpoint property address or use GPS
                      </p>
                    </div>

                    {/* 📍 CURRENT LOCATION BUTTON */}
                    <button
                      type="button"
                      onClick={handleCurrentLocation}
                      disabled={isLocating}
                      className="bg-gradient-to-r from-[#24ADD7] to-cyan-500 hover:from-[#1b8fae] hover:to-cyan-600 active:scale-95 text-white px-4 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/25 transition-all cursor-pointer w-fit"
                    >
                      <FiMapPin className="text-base animate-bounce" />
                      {isLocating ? "Detecting GPS..." : "Use My Current Location"}
                    </button>
                  </div>

                  {/* ROW 1: STATE, CITY, PINCODE */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="label">State</label>
                      <select
                        value={selectedState}
                        onChange={(e) => handleStateChange(e.target.value)}
                        className="input-field mt-2 cursor-pointer font-bold"
                      >
                        <option value="">Select State</option>
                        {INDIA_STATES.map((st) => (
                          <option key={st} value={st}>
                            {st}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="label">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="city"
                        placeholder="e.g. Gwalior"
                        value={form.city}
                        onChange={handleChange}
                        list="cityDataList"
                        className="input-field mt-2"
                      />
                      <datalist id="cityDataList">
                        {filteredCities.map((c) => (
                          <option key={c} value={c} />
                        ))}
                      </datalist>
                      {errors.city && (
                        <p className="text-[10px] text-red-500 font-bold mt-1 uppercase">
                          {errors.city}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="label">
                        Pincode <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="pincode"
                        placeholder="e.g. 474001"
                        value={form.pincode}
                        onChange={handleChange}
                        className="input-field mt-2"
                      />
                      {errors.pincode && (
                        <p className="text-[10px] text-red-500 font-bold mt-1 uppercase">
                          {errors.pincode}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ROW 2: HOUSE NO + LOCALITY */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">House No / Tower / Flat</label>
                      <input
                        type="text"
                        name="houseNumber"
                        placeholder="e.g. Flat 302, Tower B"
                        value={form.houseNumber}
                        onChange={handleChange}
                        className="input-field mt-2"
                      />
                    </div>

                    <div>
                      <label className="label">
                        Locality / Area <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="localityArea"
                        placeholder="e.g. City Center, Morar"
                        value={form.localityArea}
                        onChange={handleChange}
                        className="input-field mt-2"
                      />
                      {errors.localityArea && (
                        <p className="text-[10px] text-red-500 font-bold mt-1 uppercase">
                          {errors.localityArea}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ROW 3: FULL ADDRESS */}
                  <div>
                    <label className="label">
                      Full Property Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={2}
                      name="propertyAddress"
                      placeholder="Detailed address with street name, landmark, etc..."
                      value={form.propertyAddress}
                      onChange={handleChange}
                      className="input-field mt-2"
                    />
                    {errors.propertyAddress && (
                      <p className="text-[10px] text-red-500 font-bold mt-1 uppercase">
                        {errors.propertyAddress}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 3: SPECIFICATIONS */}
              {step === 3 && (
                <div className="space-y-6 animate-in fade-in duration-300">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 tracking-tight">Specifications & Pricing</h2>
                    <p className="text-xs font-bold text-gray-400 mt-0.5 uppercase tracking-wide">
                      Set pricing, measurements & room layout
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* PRICE */}
                    <div>
                      <div className="flex justify-between items-center">
                        <label className="label">
                          {form.listingCategory === "rent" ? "Monthly Rent (₹)" : "Selling Price (₹)"}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        {form.price && (
                          <span className="text-[11px] font-black text-[#24ADD7]">
                            {formatPriceWords(form.price)}
                          </span>
                        )}
                      </div>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-black text-sm">
                          ₹
                        </span>
                        <input
                          type="number"
                          name="price"
                          placeholder="e.g. 5000000"
                          value={form.price}
                          onChange={handleChange}
                          className="input-field pl-9"
                        />
                      </div>
                      {errors.price && (
                        <p className="text-[10px] text-red-500 font-bold mt-1 uppercase">
                          {errors.price}
                        </p>
                      )}
                    </div>

                    {/* AREA */}
                    <div>
                      <label className="label">
                        Property Area (Sq.Ft) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative mt-2">
                        <input
                          type="number"
                          name="area"
                          placeholder="e.g. 1500"
                          value={form.area}
                          onChange={handleChange}
                          className="input-field pr-16"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-black text-xs uppercase">
                          Sq.Ft
                        </span>
                      </div>
                      {errors.area && (
                        <p className="text-[10px] text-red-500 font-bold mt-1 uppercase">
                          {errors.area}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* BEDROOMS (BHK PILLS) */}
                  <div>
                    <label className="label">Bedrooms (BHK)</label>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
                      {["1", "2", "3", "4", "5", "6+"].map((bhk) => (
                        <button
                          key={bhk}
                          type="button"
                          onClick={() => setForm({ ...form, bedRoom: bhk })}
                          className={`py-3 rounded-2xl text-xs font-black uppercase tracking-wider border-2 transition-all cursor-pointer ${
                            form.bedRoom === bhk
                              ? "border-[#24ADD7] bg-[#24ADD7] text-white shadow-md shadow-cyan-300/40"
                              : "border-gray-200 text-gray-700 bg-gray-50/50 hover:bg-white"
                          }`}
                        >
                          {bhk} BHK
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ROOM SPECS */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="label">Bathrooms</label>
                      <select
                        name="bathrooms"
                        value={form.bathrooms}
                        onChange={handleChange}
                        className="input-field mt-2 cursor-pointer font-bold"
                      >
                        {["1", "2", "3", "4", "5+"].map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="label">Balconies</label>
                      <select
                        name="balcony"
                        value={form.balcony}
                        onChange={handleChange}
                        className="input-field mt-2 cursor-pointer font-bold"
                      >
                        {["0", "1", "2", "3", "4+"].map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="label">Parking</label>
                      <select
                        name="parking"
                        value={form.parking}
                        onChange={handleChange}
                        className="input-field mt-2 cursor-pointer font-bold"
                      >
                        {["1", "2", "3", "4+"].map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {form.listingCategory === "rent" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                      <div>
                        <label className="label">Security Deposit (₹)</label>
                        <input
                          type="number"
                          name="securityDeposit"
                          placeholder="e.g. 20000"
                          value={form.securityDeposit}
                          onChange={handleChange}
                          className="input-field mt-2"
                        />
                      </div>
                      <div>
                        <label className="label">Available From</label>
                        <input
                          type="date"
                          name="availableFrom"
                          value={form.availableFrom}
                          onChange={handleChange}
                          className="input-field mt-2"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 4: AMENITIES & LEGAL */}
              {step === 4 && (
                <div className="space-y-6 animate-in fade-in duration-300">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 tracking-tight">Amenities & Features</h2>
                    <p className="text-xs font-bold text-gray-400 mt-0.5 uppercase tracking-wide">
                      Select furnishing status and highlight facilities
                    </p>
                  </div>

                  {/* FURNISHING STATUS */}
                  <div>
                    <label className="label">Furnishing Status</label>
                    <div className="grid grid-cols-3 gap-3 mt-2">
                      {[
                        { label: "Furnished", value: "furnished" },
                        { label: "Semi Furnished", value: "semi-furnished" },
                        { label: "Unfurnished", value: "unfurnished" },
                      ].map((item) => (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => setForm({ ...form, furnishing: item.value })}
                          className={`py-3.5 px-3 rounded-2xl text-xs font-black uppercase tracking-wider border-2 transition-all cursor-pointer ${
                            form.furnishing === item.value
                              ? "border-[#24ADD7] bg-[#24ADD7] text-white shadow-md shadow-cyan-300/40"
                              : "border-gray-200 text-gray-700 bg-gray-50/50 hover:bg-white"
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* FURNISHING ITEMS */}
                  {form.furnishing !== "unfurnished" && (
                    <div className="animate-in fade-in duration-300">
                      <label className="label">Furnishing Items Included</label>
                      <div className="flex flex-wrap gap-2 mt-2.5">
                        {furnishingItemsList.map((item) => {
                          const isSel = furnishingItems.includes(item);
                          return (
                            <button
                              key={item}
                              type="button"
                              onClick={() => toggleFurnishingItem(item)}
                              className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                                isSel
                                  ? "bg-[#24ADD7] text-white border-[#24ADD7] shadow-sm shadow-cyan-300/30"
                                  : "bg-gray-50 text-gray-700 border-gray-200 hover:border-blue-200 hover:bg-white"
                              }`}
                            >
                              {isSel ? "✓ " : "+ "}
                              {item}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* AMENITIES */}
                  <div>
                    <label className="label">Community & Building Amenities</label>
                    <div className="flex flex-wrap gap-2 mt-2.5">
                      {amenityList.map((item) => {
                        const isSel = amenities.includes(item);
                        return (
                          <button
                            key={item}
                            type="button"
                            onClick={() => toggleAmenity(item)}
                            className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                              isSel
                                ? "bg-[#24ADD7] text-white border-[#24ADD7] shadow-sm shadow-cyan-300/30"
                                : "bg-gray-50 text-gray-700 border-gray-200 hover:border-blue-200 hover:bg-white"
                            }`}
                          >
                            {isSel ? "✓ " : "+ "}
                            {item}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* NEARBY LANDMARKS */}
                  <div className="pt-2">
                    <div className="flex justify-between items-center mb-2">
                      <label className="label">Nearby Landmarks (Around Project)</label>
                      <button
                        type="button"
                        onClick={() =>
                          setAroundProject([...aroundProject, { name: "", details: "" }])
                        }
                        className="text-xs font-black text-[#24ADD7] hover:underline flex items-center gap-1 cursor-pointer uppercase tracking-wider"
                      >
                        <FiPlus /> Add Place
                      </button>
                    </div>

                    <div className="space-y-2.5">
                      {aroundProject.map((item, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <input
                            type="text"
                            placeholder="e.g. Metro Station / School"
                            value={item.name}
                            onChange={(e) => handleAroundChange(idx, "name", e.target.value)}
                            className="input-field text-xs py-2.5 flex-1"
                          />
                          <input
                            type="text"
                            placeholder="e.g. 500 Meters / 2 KM"
                            value={item.details}
                            onChange={(e) => handleAroundChange(idx, "details", e.target.value)}
                            className="input-field text-xs py-2.5 w-40"
                          />
                          {aroundProject.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                setAroundProject(aroundProject.filter((_, i) => i !== idx))
                              }
                              className="p-2.5 text-red-500 hover:bg-red-50 rounded-xl transition"
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 5: MEDIA & DESCRIPTION */}
              {step === 5 && (
                <div className="space-y-6 animate-in fade-in duration-300">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 tracking-tight">Photos & Description</h2>
                    <p className="text-xs font-bold text-gray-400 mt-0.5 uppercase tracking-wide">
                      Upload high quality property pictures and describe features
                    </p>
                  </div>

                  {/* PHOTO UPLOAD BOX */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="label">
                        Property Photos <span className="text-red-500">*</span>
                      </label>
                      <span className="text-[10px] font-black uppercase text-gray-400 tracking-wider">
                        {form.uploadedPhotos.length} Photos Uploaded
                      </span>
                    </div>

                    <div className="border-2 border-dashed border-gray-300 hover:border-[#24ADD7] rounded-3xl p-6 text-center bg-gray-50/50 hover:bg-cyan-50/20 transition-all cursor-pointer">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="w-full text-xs text-gray-500 file:mr-4 file:py-2.5 file:px-6 file:rounded-xl file:border-0 file:text-xs file:font-black file:bg-[#24ADD7] file:text-white file:cursor-pointer cursor-pointer"
                      />
                      {uploading && (
                        <p className="text-xs text-[#24ADD7] font-bold mt-2 animate-pulse">
                          Uploading photos, please wait...
                        </p>
                      )}
                    </div>
                    {errors.uploadedPhotos && (
                      <p className="text-[10px] text-red-500 font-bold mt-1 uppercase">
                        {errors.uploadedPhotos}
                      </p>
                    )}

                    {/* PHOTO PREVIEW CARDS */}
                    {form.uploadedPhotos.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-4">
                        {form.uploadedPhotos.map((img, i) => (
                          <div
                            key={i}
                            className="relative w-24 h-24 rounded-2xl overflow-hidden border border-gray-200 shadow-sm group"
                          >
                            <img
                              src={img}
                              alt="Property"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              className="absolute top-1.5 right-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-full w-5 h-5 text-[10px] flex items-center justify-center font-bold shadow-md cursor-pointer transition"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* DEEP DESCRIPTION */}
                  <div>
                    <label className="label mb-2">Deep Property Description</label>
                    <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-inner">
                      <JoditEditor
                        value={description}
                        onBlur={(newContent) => setDescription(newContent)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* STICKY BOTTOM NAVIGATION BAR */}
            <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-between items-center">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-6 py-3 rounded-2xl text-xs font-black text-gray-500 uppercase tracking-widest hover:text-gray-900 transition cursor-pointer flex items-center gap-1.5"
                >
                  <FiArrowLeft /> Back
                </button>
              ) : (
                <div />
              )}

              {step < 5 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (validateStep(step)) {
                      setStep(step + 1);
                    }
                  }}
                  className="px-8 py-3.5 rounded-2xl font-black uppercase text-xs tracking-widest bg-[#24ADD7] hover:bg-[#1b8fae] active:scale-95 text-white shadow-xl shadow-cyan-500/25 transition-all cursor-pointer flex items-center gap-2"
                >
                  Save & Continue <FiArrowRight />
                </button>
              ) : (
                <button
                  type="button"
                  disabled={loading || uploading}
                  onClick={handleSubmit}
                  className="px-10 py-3.5 rounded-2xl font-black uppercase text-xs tracking-widest bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white shadow-xl shadow-emerald-500/25 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <FiCheckCircle /> Publish Property
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        .label {
          display: block;
          font-size: 0.72rem;
          font-weight: 900;
          color: #374151;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .input-field {
          width: 100%;
          border: 2px solid #F1F3F5;
          background: #FAFAFB;
          border-radius: 1rem;
          padding: 0.75rem 1rem;
          font-weight: 700;
          font-size: 0.85rem;
          color: #111827;
          outline: none;
          transition: all 0.2s;
        }
        .input-field:focus {
          border-color: #24ADD7;
          background: #FFFFFF;
          box-shadow: 0 0 0 3px rgba(36, 173, 215, 0.12);
        }
      `}</style>
    </div>
  );
}
